import { useCallback } from 'react';
import { 
  generateStatusId,
  getSuggestedStatusColor,
} from '../../FlowchartView/utils/statusUtils';

/**
 * useViewPropsHandlers Hook
 * 
 * Contains shared handlers used across multiple view props:
 * - Delete task with nested check
 * - Bulk delete with nested check
 * - Icon change with nested sync
 * - Add kanban column
 */
const useViewPropsHandlers = ({
  tasks,
  statuses,
  actions,
  handlers,
  modals,
  nested,
  handleTrashNestedSprint,
  onAnySprintIconChange,
  setEditingKanbanColumnId,
}) => {
  // Wrapper for delete that handles nodes with nested campaigns
  // options (e.g. { bridge: true }) are forwarded to the delete handlers
  const handleDeleteTaskWithNestedCheck = useCallback((taskId, options) => {
    const task = tasks.find(t => t.id === taskId);
    
    if (task?.nestedSprintId) {
      const nestedSprint = nested.nestedSprints?.find(ns => ns.id === task.nestedSprintId);
      modals.handleOpenDeleteModal(taskId, nestedSprint?.name, options);
    } else {
      actions.handleDeleteTask(taskId, options);
    }
  }, [tasks, actions, nested.nestedSprints, modals]);

  // Actually perform the delete after modal confirmation
  const handleConfirmDeleteTaskWithNested = useCallback((taskId, options) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.nestedSprintId) {
      handleTrashNestedSprint(taskId);
    }
    actions.handleDeleteTask(taskId, options);
  }, [tasks, actions, handleTrashNestedSprint]);

  // Wrapper for bulk delete that handles nodes with nested campaigns
  const handleDeleteTasksWithNestedCheck = useCallback((taskIds, options) => {
    if (!taskIds || taskIds.length === 0) return;
    
    const tasksWithNested = [];
    const tasksWithoutNested = [];
    
    taskIds.forEach(taskId => {
      const task = tasks.find(t => t.id === taskId);
      if (task?.nestedSprintId) {
        tasksWithNested.push({ taskId, nestedSprintId: task.nestedSprintId, taskName: task.name });
      } else {
        tasksWithoutNested.push(taskId);
      }
    });
    
    tasksWithNested.forEach(({ taskId }) => {
      handleTrashNestedSprint(taskId);
      actions.handleDeleteTask(taskId, options);
    });
    
    if (tasksWithoutNested.length > 0) {
      actions.handleDeleteTasks(tasksWithoutNested, options);
    }
  }, [tasks, actions, handleTrashNestedSprint]);

  // Wrapper for icon change that handles nested sprint sync
  const handleIconChangeWithNestedSync = useCallback((taskId, newIcon) => {
    const task = tasks.find(t => t.id === taskId);
    
    if (task?.nestedSprintId && onAnySprintIconChange) {
      onAnySprintIconChange(task.nestedSprintId, newIcon);
    } else {
      actions.handleIconChange(taskId, newIcon);
    }
  }, [tasks, actions, onAnySprintIconChange]);

  // Handle adding a new kanban column (status) from toolbar
  const handleAddKanbanColumn = useCallback(() => {
    const newStatus = {
      id: generateStatusId('New Status', statuses),
      name: 'New Status',
      category: 'in_progress',
      color: getSuggestedStatusColor(statuses),
      order: statuses.length,
    };
    
    handlers.handleAddStatus(newStatus);
    setEditingKanbanColumnId(newStatus.id);
    
    setTimeout(() => {
      const board = document.querySelector('.kanban-board');
      if (board) {
        board.scrollTo({ left: board.scrollWidth, behavior: 'smooth' });
      }
    }, 50);
  }, [statuses, handlers, setEditingKanbanColumnId]);

  return {
    handleDeleteTaskWithNestedCheck,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    handleIconChangeWithNestedSync,
    handleAddKanbanColumn,
  };
};

export default useViewPropsHandlers;
